import produce from 'immer';
import { DEFAULT_CART_PRODUCT_STRUCTURE } from './index';
import { PAYMENT_BILLABLE, PAYMENT_COMP, PAYMENT_UNIT } from '../../shop/constants';
import { ADDON_CART_VALIDATION } from '../../util/actionAddons';
import { findProductInfo } from '../../productsInfo';
import { sortList } from '../../util/sortList';
import { filterList } from '../../util';
import { isEqual } from '../../util/lodash';

export const getPaymentMethodConds = paymentMethod => ({
  isBillable: paymentMethod === PAYMENT_BILLABLE,
  isComp: paymentMethod === PAYMENT_COMP,
  isUnit: paymentMethod === PAYMENT_UNIT
});

export const computeCartTotals = (action, cart) => {
  const totals = cart.products.reduce(
    (acc, product) => {
      const {
        totals: { unitCount, retail, wholesale },
        paymentMethod,
        unitAllotmentId
      } = product;
      const { isComp, isBillable, isUnit } = getPaymentMethodConds(paymentMethod);

      return {
        retail: acc.retail + retail,
        wholesale: acc.wholesale + wholesale,
        unitCount: acc.unitCount + unitCount,
        billable: isBillable ? acc.billable + wholesale : acc.billable,
        comp: isComp ? acc.comp + wholesale : acc.comp,
        unitAllotments:
          isUnit && unitAllotmentId
            ? {
                ...acc.unitAllotments,
                [unitAllotmentId]: (acc.unitAllotments[unitAllotmentId] || 0) + unitCount
              }
            : acc.unitAllotments,
        productCount: acc.productCount + 1
      };
    },
    {
      billable: 0,
      comp: 0,
      retail: 0,
      wholesale: 0,
      unitCount: 0,
      unitAllotments: {},
      productCount: 0
    }
  );

  if (!isEqual(cart.totals, totals)) {
    return { ...cart, totals };
  }

  return cart;
};

export const computeProductTotals = (action, product) => {
  if (!action.addon[ADDON_CART_VALIDATION]) {
    throw new Error(`The Cart Validation Addon is required for the event type: ${action.type}`);
  }

  const { productsInfoList } = action.addon[ADDON_CART_VALIDATION];
  const productInfo = findProductInfo(productsInfoList, product.productCode);
  const prices = { retail: 0, wholesale: 0, ...productInfo?.prices };
  const quantity = product.sizes.reduce((acc, size) => acc + size.quantity, 0);
  const totals = {
    ...DEFAULT_CART_PRODUCT_STRUCTURE.totals,
    retail: quantity * prices.retail,
    wholesale: quantity * prices.wholesale,
    unitCount: quantity
  };

  if (!isEqual(product.totals, totals)) {
    return { ...product, totals };
  }

  return product;
};

export const sortAndFilterCartProducts = (action, cart) => {
  if (!action.addon[ADDON_CART_VALIDATION]) {
    throw new Error(`The Cart Validation Addon is required for the event type: ${action.type}`);
  }
  const { productsInfoList } = action.addon[ADDON_CART_VALIDATION];
  const {
    products,
    searchBy,
    sortBy: { value, isNumber, desc }
  } = cart;
  const productsWithInfo = products.map(product => {
    const productInfo = findProductInfo(productsInfoList, product.productCode);

    return {
      ...productInfo,
      ...product
    };
  });
  const filteredProducts = filterList(productsWithInfo, ['name', 'productCode'], searchBy);
  let sortedProducts;
  if (value === 'createdAt' && desc) {
    const filteredProductsCopy = [...filteredProducts];
    sortedProducts = filteredProductsCopy.reverse();
  } else {
    sortedProducts = sortList(filteredProducts, value, isNumber, !desc);
  }
  const sortedAndFilteredProductIds = sortedProducts.map(product => product.id);

  if (!isEqual(cart.sortedAndFilteredProductIds, sortedAndFilteredProductIds)) {
    return { ...cart, sortedAndFilteredProductIds };
  }

  return cart;
};

export const setProductQuantity = (product, sizeDescription, newQuantity, sizeDisplay) =>
  produce(product, draftProduct => {
    const qty = +newQuantity;
    const size = draftProduct.sizes.find(({ sizeCode }) => sizeCode === sizeDescription);
    if (size) {
      size.quantity = qty;
      size.sizeDisplay = sizeDisplay || '';
    } else {
      draftProduct.sizes.push({ sizeCode: sizeDescription, quantity: qty, sizeDisplay: sizeDisplay || '' });
    }
  });

export const setCartHeader = (cart, name, value) => ({
  ...cart,
  headers: { ...cart.headers, [name]: value },
  pendingSave: true
});
